import { CurrencyInput, FieldWrapper, Select } from 'components/inputs'
import { ButtonGroup } from 'ui/inputs/ButtonGroup'
import { RangeField } from './Range/RangeField'
import { Expandable } from 'components/layout/Expandable'
import { Switcher } from 'components/layout/primitives/Switcher'
import {
	ApprovabilityRequest,
	ApprovabilityRequestCreditScoreEnum,
	ApprovabilityRequestPurposeEnum
} from 'generated/api'
import { useDebouncedApprovabilityRequest } from '../store'
import { useEffect } from 'react'
import { TriggerFactory } from 'components/layout/Expandable/components/DefaultTrigger'
import { Controller, useForm } from 'react-hook-form'

const TriggerComponent = TriggerFactory({ collapsedText: 'Add More Details ', expandedText: 'Close' })

const initialData: Required<Omit<ApprovabilityRequest, 'state'>> = {
	creditScore: 'good',
	loanAmount: 10000,
	annualIncome: 60000,
	purpose: 'debt_consolidation'
}

const creditScoreOptions: { value: ApprovabilityRequestCreditScoreEnum; label: string; hint: string }[] = [
	{ value: 'excellent', label: 'Excellent', hint: '720+' },
	{ value: 'good', label: 'Good', hint: '660 - 719' },
	{ value: 'fair', label: 'Fair', hint: '620 - 659' },
	{ value: 'poor', label: 'Poor', hint: ' < 620' }
]

const purposeOptions: { value: ApprovabilityRequestPurposeEnum; label: string }[] = [
	{ value: 'auto_purchase', label: 'Auto Purchase' },
	{ value: 'auto_refinance', label: 'Auto Refinance' },
	{ value: 'baby', label: 'Baby' },
	{ value: 'boat', label: 'Boat' },
	{ value: 'business', label: 'Business' },
	{ value: 'car_repair', label: 'Car Repair' },
	{ value: 'cosmetic', label: 'Cosmetic' },
	{ value: 'credit_card_refi', label: 'Credit Card Refi' },
	{ value: 'debt_consolidation', label: 'Debt Consolidation' },
	{ value: 'emergency', label: 'Emergency' },
	{ value: 'engagement', label: 'Engagement' },
	{ value: 'green', label: 'Green' },
	{ value: 'home_improvement', label: 'Home Improvement' },
	{ value: 'home_purchase', label: 'Home Purchase' },
	{ value: 'home_refi', label: 'Home Refi' },
	{ value: 'household_expenses', label: 'Household Expenses' },
	{ value: 'large_purchases', label: 'Large Purchases' },
	{ value: 'life_event', label: 'Life Event' },
	{ value: 'medical_dental', label: 'Medical Dental' },
	{ value: 'motorcycle', label: 'Motorcycle' },
	{ value: 'moving_relocation', label: 'Moving Relocation' },
	{ value: 'rv', label: 'Rv' },
	{ value: 'special_occasion', label: 'Special Occasion' },
	{ value: 'student_loan', label: 'Student Loan' },
	{ value: 'student_loan_refi', label: 'Student Loan Refi' },
	{ value: 'taxes', label: 'Taxes' },
	{ value: 'vacation', label: 'Vacation' },
	{ value: 'wedding', label: 'Wedding' },
	{ value: 'other', label: 'Other' }
]

export const Form = () => {
	const fetchResults = useDebouncedApprovabilityRequest()
	const { register, control, watch, handleSubmit } = useForm({ defaultValues: initialData, mode: 'onChange' })

	// Submits the form onChange event
	useEffect(() => {
		const subscription = watch(() => {
			handleSubmit(fetchResults)()
		})
		return () => subscription.unsubscribe()
	}, [handleSubmit, watch, fetchResults])

	return (
		<form onSubmit={e => e.preventDefault()}>
			<Expandable Trigger={TriggerComponent}>
				<Expandable.Static>
					<Switcher>
						<FieldWrapper label="Credit Score" htmlFor={'creditScore'} space="small">
							<Controller
								name={'creditScore'}
								control={control}
								render={({ field: { onChange, value } }) => (
									<ButtonGroup value={value} onChange={e => onChange(e)} options={creditScoreOptions} />
								)}
							/>
						</FieldWrapper>
						<Controller
							name={'annualIncome'}
							control={control}
							render={({ field: { onChange, value } }) => {
								const options = { min: 10000, max: 150000, step: 1000 }

								return (
									<RangeField
										name="annualIncome"
										label="Annual Income"
										value={value}
										onChange={e => onChange(parseInt(e.currentTarget.value))}
										options={options}
										valueFormatter={(value: number) => `$${value / 1000}K${value === options.max ? '+' : ''}`}
										space="small"
									/>
								)
							}}
						/>
					</Switcher>
				</Expandable.Static>
				<Expandable.Extra>
					<Switcher>
						<FieldWrapper label={'Loan Amount'} htmlFor={'amount'}>
							<Controller
								name={'loanAmount'}
								control={control}
								render={({ field: { name, value, onChange, onBlur } }) => (
									<CurrencyInput
										id={'amount'}
										name={name}
										value={value}
										onChange={e => onChange(parseInt(e.currentTarget.value))}
										onBlur={onBlur}
									/>
								)}
							/>
						</FieldWrapper>
						<FieldWrapper label={'Loan Purpose'} htmlFor={'purpose'}>
							<Select {...register('purpose')} id={'purpose'} options={purposeOptions} />
						</FieldWrapper>
					</Switcher>
				</Expandable.Extra>
			</Expandable>
		</form>
	)
}
